import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  type ThemingProps,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import debounce from "lodash/debounce";
import truncate from "lodash/truncate";
import React from "react";
import { useTranslation } from "react-i18next";
import type { onErrorCb } from "@/api/types";
import { InputStyled, SpinnerStyled } from "@/components/atoms";
import { ChevronDownIcon, LoopIcon } from "@/components/icons";
import { useGetAvailableSpace } from "@/lib/hooks";

type ItemType = { id: string | number; displayId?: string | number };

type FetchFunctionParams = {
  search?: string;
  onError?: onErrorCb;
  signal?: AbortSignal;
};

interface Props<T extends ItemType> {
  title: string;
  selectedItem: T["id"] | undefined;
  fetchItems: (params: FetchFunctionParams) => Promise<T[]>;
  displayFieldKey: keyof T;
  selectedFieldDisplayKey?: keyof T;
  fetchOnOpen?: boolean;
  showDisplayId?: boolean;
  defaultValue?: T["id"] | undefined | null;
  onChange: (items: T["id"] | undefined | null) => void;
  variant?: ThemingProps<"Popover">["variant"];
  labelFullWidth?: boolean;
  isError?: boolean;
  isDisabled?: boolean;
  btnBorderColor?: string;
  alwaysShowResetBtn?: boolean;
  styles?: React.CSSProperties;
}

export const DropdownAsync = <T extends ItemType>(props: Props<T>) => {
  const { fetchOnOpen = true } = props;
  const { t } = useTranslation();
  const toast = useToast();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [allItems, setAllItems] = React.useState<T[]>([]);

  const selectedItemRef = React.useRef<T | undefined>();

  const abortRef = React.useRef<AbortController | null>(null);

  const [searchTerm, setSearchTerm] = React.useState<string>("");

  const selectedItemDisplay = React.useMemo(() => {
    if (props.selectedItem == undefined) {
      return undefined;
    }

    return selectedItemRef.current?.id === props.selectedItem
      ? selectedItemRef.current[props.selectedFieldDisplayKey ?? props.displayFieldKey]
      : props.selectedItem;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedItem, props.displayFieldKey, props.selectedFieldDisplayKey, allItems]);

  const initialFocusRef = React.useRef<HTMLInputElement>(null);

  const { onOpen, onClose, isOpen } = useDisclosure();

  const displayedItems = React.useMemo(() => {
    if (props.selectedItem == undefined) {
      return allItems;
    }

    const selectedItemData =
      selectedItemRef.current?.id === props.selectedItem ? selectedItemRef.current : null;

    const selectedItemMock =
      selectedItemData ??
      ({
        id: props.selectedItem,
        [props.displayFieldKey]: props.selectedItem,
      } as T);

    const allItemsWithSelected: T[] = selectedItemData
      ? allItems
      : [selectedItemMock].concat(allItems.filter((item) => item.id !== props.selectedItem));

    return allItemsWithSelected;
  }, [allItems, props.displayFieldKey, props.selectedItem]);

  const parentRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: displayedItems.length,
    getScrollElement: () => parentRef.current,
    getItemKey: (i) => displayedItems[i].id,
    estimateSize: () => 45,
    overscan: 5,
  });

  const toggleItem = React.useCallback(
    (id: T["id"]) => {
      const item = displayedItems.find((item) => item.id === id);

      if (item) {
        selectedItemRef.current = item;
      }

      props.onChange(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayedItems, onClose, props.onChange],
  );

  const handleResetFilter = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setSearchTerm("");
      props.onChange(props.defaultValue);
      void fetchItemsList("");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange, props.defaultValue],
  );

  const fetchItemsList = React.useCallback(
    async (search?: string) => {
      try {
        setIsLoading(true);

        abortRef.current?.abort();
        abortRef.current = new AbortController();

        const items = await props.fetchItems({
          search,
          onError: (type) => {
            if (type === "abort") {
              return;
            }

            toast({
              title: t("common.errors.fetch.title", { defaultValue: "Не удалось получить данные" }),
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          },
          signal: abortRef.current.signal,
        });

        setAllItems(items ?? []);

        items?.forEach((item) => {
          if (item.id === selectedItemRef.current?.id) {
            selectedItemRef.current = item;
          }
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.fetchItems, t, toast],
  );

  const setSearchDebounce = React.useMemo(() => {
    return debounce((search: string) => {
      if (!search.trim() && !fetchOnOpen) {
        setAllItems([]);
        return;
      }
      void fetchItemsList(search.trim());
    }, 500);
  }, [fetchItemsList, fetchOnOpen]);

  React.useEffect(() => {
    return () => {
      setSearchDebounce.cancel();
    };
  }, [setSearchDebounce]);

  const onPopoverClose = React.useCallback(() => {
    onClose();
    setSearchTerm("");
    setSearchDebounce.cancel();
  }, [onClose, setSearchDebounce]);

  React.useEffect(() => {
    if (isOpen && fetchOnOpen) {
      void fetchItemsList("");
    }
  }, [isOpen, fetchItemsList, fetchOnOpen]);

  React.useEffect(() => {
    if (props.selectedItem != undefined) {
      void fetchItemsList("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    selectedItemRef.current = {
      id: props.selectedItem,
      [props.displayFieldKey]: props.selectedItem,
      ...selectedItemRef.current,
    } as T;
  }, [props.displayFieldKey, props.selectedItem]);

  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const popoverMaxHeight = useGetAvailableSpace(isOpen, triggerRef);

  return (
    <>
      <Popover
        placement="bottom-start"
        onOpen={onOpen}
        onClose={onPopoverClose}
        flip={true}
        isOpen={isOpen}
        initialFocusRef={initialFocusRef}
        variant={props.variant}
      >
        <PopoverTrigger>
          <Button
            isDisabled={props.isDisabled}
            size="sm"
            colorScheme="gray"
            variant="outline"
            rightIcon={
              <ChevronDownIcon style={isOpen ? { transform: "rotate(180deg)" } : undefined} />
            }
            borderColor={props.isError ? "red-error" : (props.btnBorderColor ?? undefined)}
            borderRadius={"12px"}
            ref={triggerRef}
            style={{
              boxShadow: props.isError ? "0 0 0 1px var(--chakra-colors-red-error)" : undefined,
              ...props.styles,
            }}
          >
            <Box
              display="inline-flex"
              alignItems="center"
              gap="5px"
              width={props.labelFullWidth ? "100%" : undefined}
              overflow={"hidden"}
            >
              <Text variant="text_sm_normal" overflow={"hidden"} textOverflow="ellipsis">
                {(selectedItemDisplay as string) ?? props.title}
              </Text>
            </Box>
          </Button>
        </PopoverTrigger>
        <Portal>
          {isOpen && (
            <PopoverContent
              backgroundColor={"elevation-bg"}
              shadow={
                "0 1.5rem 1rem #00000008,0 .75rem .75rem #0000000a,0 .25rem .375rem #0000000d"
              }
            >
              <PopoverBody position={"relative"}>
                {isLoading && (
                  <Flex
                    w="100%"
                    h="100%"
                    align="center"
                    justify="center"
                    zIndex={1}
                    position={"absolute"}
                    top={0}
                    left={0}
                  >
                    <SpinnerStyled zIndex={1} />
                    <Flex
                      w="100%"
                      h="100%"
                      bg={"whiteAlpha.500"}
                      _dark={{ bg: "whiteAlpha.300" }}
                      position="absolute"
                    ></Flex>
                  </Flex>
                )}
                <InputStyled
                  ref={initialFocusRef}
                  placeholder={t("common.search", { defaultValue: "Поиск" })}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setSearchDebounce(e.target.value.trim());
                  }}
                  fullW
                  leftElement={
                    <InputLeftElement h="100%" w="auto" px="5px" color="gray-base-06">
                      <LoopIcon h={20} w={20} />
                    </InputLeftElement>
                  }
                />

                <Flex
                  direction={"column"}
                  marginTop={"0.5rem"}
                  maxH={`min(${popoverMaxHeight}, 60vh)`}
                  // maxH={`min(${"500px"}, 60vh)`}
                  // height={"500px"}
                  overflow={"auto"}
                  ref={parentRef}
                >
                  <div
                    style={{
                      height: `${rowVirtualizer.getTotalSize()}px`,
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                      <Flex
                        as="label"
                        key={virtualRow.index}
                        width="100%"
                        flex={1}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        gap={".5rem"}
                        padding={".3125rem .5rem"}
                        cursor={"pointer"}
                        borderRadius={"8px"}
                        _hover={{ backgroundColor: "gray-base-03" }}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          // height: `10px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        <Flex direction="column">
                          {displayedItems[virtualRow.index].id != undefined && (
                            <Text variant="text_xxs__normal" color={"text-base-02"}>
                              {displayedItems[virtualRow.index].id}

                              {props.showDisplayId &&
                                displayedItems[virtualRow.index].displayId &&
                                ` | ${displayedItems[virtualRow.index].displayId}`}
                            </Text>
                          )}
                          <Text
                            variant="text_sm_normal"
                            title={String(displayedItems[virtualRow.index][props.displayFieldKey])}
                          >
                            {truncate(
                              String(displayedItems[virtualRow.index][props.displayFieldKey]),
                              {
                                length: 30,
                              },
                            )}
                          </Text>
                        </Flex>
                        <Checkbox
                          colorScheme="brand"
                          value={displayedItems[virtualRow.index].id}
                          isChecked={props.selectedItem === displayedItems[virtualRow.index].id}
                          onChange={() => {
                            toggleItem(displayedItems[virtualRow.index].id);
                          }}
                        ></Checkbox>
                      </Flex>
                    ))}
                  </div>
                </Flex>

                {displayedItems.length !== 0 && props.selectedItem && <Divider />}

                {displayedItems.length === 0 && !isLoading && (
                  <Text m={2} variant="text_sm_normal">
                    {t("common.nothingFound", { defaultValue: "Ничего не найдено" })}
                  </Text>
                )}
                {(props.selectedItem != undefined || props.alwaysShowResetBtn) && !isLoading && (
                  <Button
                    w="100%"
                    size="sm"
                    variant="ghost"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleResetFilter(e)}
                    fontWeight={400}
                    _hover={{}}
                  >
                    {t("common.reset", { defaultValue: "Сбросить" })}
                  </Button>
                )}
              </PopoverBody>
            </PopoverContent>
          )}
        </Portal>
      </Popover>
    </>
  );
};
